/** The routes of the sector workspaces: `/`, `/:sector` and a page one sector has. */
import { useEffect, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { lastSectorCode, rememberSector, SectorContext, sectorPath, useSector, useSectors, type Sector } from './sector'

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
    </div>
  )
}

/** `/` and old links: the sector last used, else the first. */
export function SectorRedirect({ page = '' }: { page?: string }) {
  const { data: sectors, isLoading } = useSectors()
  const location = useLocation()
  if (isLoading || !sectors) return <PageLoader />
  if (sectors.length === 0) {
    return <p className="py-24 text-center font-inter text-body text-ink-muted">No sectors are active.</p>
  }
  const last = lastSectorCode()
  const code = sectors.some((s) => s.code === last) ? (last as string) : sectors[0].code
  return <Navigate to={`${sectorPath(code, page)}${location.search}`} replace />
}

/** The `/:sector` route: checks the sector and gives it to the pages below. */
export function SectorScope() {
  const { sector: code } = useParams()
  const { data: sectors, isLoading } = useSectors()
  const sector = sectors?.find((s) => s.code === code) ?? null

  useEffect(() => {
    if (sector) rememberSector(sector.code)
  }, [sector])

  if (isLoading || !sectors) return <PageLoader />
  if (!sector) return <SectorRedirect />
  return (
    <InSector sector={sector}>
      <Outlet />
    </InSector>
  )
}

/** Gives a sector to what is inside it (the `/:sector` route, and tests). */
export function InSector({ sector, children }: { sector: Sector; children: ReactNode }) {
  return <SectorContext.Provider value={sector}>{children}</SectorContext.Provider>
}

/** A page one sector has (Education's curriculum): elsewhere, say so. */
export function OnlyIn({ sectors, children }: { sectors: string[]; children: ReactNode }) {
  const sector = useSector()
  const { data: all } = useSectors()
  if (sectors.includes(sector.code)) return <>{children}</>
  const names = (all ?? []).filter((s) => sectors.includes(s.code)).map((s) => s.name)
  return (
    <div className="rounded-card border border-hairline bg-surface p-8 text-center">
      <p className="font-inter text-body text-ink">
        {sector.name} has no such page. It belongs to {names.join(' and ') || 'another sector'}.
      </p>
    </div>
  )
}
